import { DecoratorNode, type DOMConversionMap, type DOMExportOutput, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from 'lexical';
import type { JSX } from 'react';
import { InlineImage } from '@/features/attachments/InlineImage';

export type SerializedImageNode = Spread<
  { attachmentId: string; src: string; altText: string },
  SerializedLexicalNode
>;

/**
 * A pasted/embedded image in a Lexical document. Bytes live as a ticket
 * attachment; the node carries the attachment id + its (auth-protected) download
 * URL. `exportDOM` emits `<img src="/api/attachments/{id}">` so the saved body
 * HTML references the attachment; rendering both here and in RichTextDisplay
 * fetches it with the bearer token (see InlineImage / RichTextDisplay).
 */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __attachmentId: string;
  __src: string;
  __altText: string;

  static getType(): string {
    return 'aes-image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__attachmentId, node.__src, node.__altText, node.__key);
  }

  constructor(attachmentId: string, src: string, altText: string, key?: NodeKey) {
    super(key);
    this.__attachmentId = attachmentId;
    this.__src = src;
    this.__altText = altText;
  }

  static importJSON(json: SerializedImageNode): ImageNode {
    return new ImageNode(json.attachmentId, json.src, json.altText);
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'aes-image',
      version: 1,
      attachmentId: this.__attachmentId,
      src: this.__src,
      altText: this.__altText
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: (el: HTMLElement) => {
          const img = el as HTMLImageElement;
          const id = img.getAttribute('data-attachment-id') ?? '';
          return { node: new ImageNode(id, img.getAttribute('src') ?? '', img.getAttribute('alt') ?? '') };
        },
        priority: 0
      })
    };
  }

  exportDOM(): DOMExportOutput {
    const img = document.createElement('img');
    img.setAttribute('src', this.__src);
    img.setAttribute('alt', this.__altText);
    img.setAttribute('data-attachment-id', this.__attachmentId);
    return { element: img };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'inline-block max-w-full my-1';
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): JSX.Element {
    return <InlineImage attachmentId={this.__attachmentId} alt={this.__altText} />;
  }
}

export function $createImageNode(attachmentId: string, src: string, altText = 'attached image'): ImageNode {
  return new ImageNode(attachmentId, src, altText);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
